import { useEffect, useState } from 'react';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { SUBJECTS, QTYPES } from '../../pages/admin/adminConstants';

type Option = { id?: string; _k?: string; text: string; isCorrect: boolean; order: number };
type Q = { id?: string; text: string; topic: string; difficulty: number; imageUrl?: string | null; explanation: string; subject: number; type: number; lessonId?: string | null; options: Option[] };

const NONE_TYPE = 2; // QuestionTypes.NoneCorrect

let optSeq = 0;
const nextOptKey = () => "opt" + (++optSeq);

function blank(subject: number, lessonId?: string | null): Q {
    return { text: "", topic: "", difficulty: 1, imageUrl: "", explanation: "", subject, type: 0, lessonId: lessonId ?? null, options: [{ text: "", isCorrect: true, order: 0, _k: nextOptKey() }, { text: "", isCorrect: false, order: 1, _k: nextOptKey() }] };
}

export default function AdminQuestionModal({ questionId, subject, lessonId, onClose, onSaved }: {
    questionId: string | null; subject: number; lessonId?: string | null; onClose: () => void; onSaved: () => void;
}) {
    const [q, setQ] = useState<Q | null>(null);
    const [imgUploading, setImgUploading] = useState(false);
    const api = new ApiHelper();

    useEffect(() => { load(); }, [questionId]);

    async function uploadImage(file: File) {
        setImgUploading(true);
        try {
            const res = await (await api.adminContentApi.upload(file)).json();
            setQ(prev => prev ? { ...prev, imageUrl: res.url } : prev);
            api.alert.success("Obrázek nahrán");
        } catch (x: any) { api.adminError(x); }
        finally { setImgUploading(false); }
    }

    async function load() {
        if (!questionId) { setQ(blank(subject, lessonId)); return; }
        try { setQ(await (await api.adminContentApi.question(questionId)).json()); } catch (x: any) { api.adminError(x); onClose(); }
    }

    function setOpt(i: number, patch: Partial<Option>) {
        if (!q) return;
        const options = q.options.map((o, idx) => idx === i ? { ...o, ...patch } : o);
        setQ({ ...q, options });
    }
    function addOpt() { if (q) setQ({ ...q, options: [...q.options, { text: "", isCorrect: false, order: q.options.length, _k: nextOptKey() }] }); }
    function removeOpt(i: number) { if (q) setQ({ ...q, options: q.options.filter((_, idx) => idx !== i).map((o, idx) => ({ ...o, order: idx })) }); }
    // Switching to the "none correct" type clears every correctness flag — the answer is the synthetic option.
    function setType(type: number) { if (q) setQ({ ...q, type, options: type === NONE_TYPE ? q.options.map(o => ({ ...o, isCorrect: false })) : q.options }); }

    async function save() {
        if (!q) return;
        if (!q.text.trim()) { api.alert.error("Zadej znění otázky"); return; }
        const options = q.options.filter(o => o.text.trim()).map((o, i) => ({ ...o, order: i }));
        if (options.length < 2) { api.alert.error("Otázka musí mít aspoň dvě možnosti"); return; }
        const correct = options.filter(o => o.isCorrect).length;
        if (q.type === NONE_TYPE) {
            if (correct !== 0) { api.alert.error("U typu „Žádná není správná“ nesmí být označena žádná možnost jako správná"); return; }
        } else if (q.type === 0) {
            if (correct !== 1) { api.alert.error("Otázka s jednou správnou odpovědí musí mít právě jednu správnou možnost"); return; }
        } else if (correct < 1) {
            api.alert.error("Označ aspoň jednu správnou odpověď"); return;
        }
        const payload = { ...q, options };
        try {
            if (q.id) await api.adminContentApi.updateQuestion(q.id, payload);
            else await api.adminContentApi.createQuestion(payload);
            api.alert.success("Otázka uložena");
            onSaved();
        } catch (x: any) { api.adminError(x); }
    }

    if (!q) return null;

    return (
        <div className="aModalOverlay" onClick={onClose}>
            <div className="aModalBox" onClick={e => e.stopPropagation()}>
                <h2>{q.id ? "Upravit otázku" : "Nová otázka"}</h2>

                <div className="aField"><label>Znění otázky</label><textarea value={q.text} onChange={e => setQ({ ...q, text: e.target.value })} /></div>
                <div className="inlineRow">
                    <div className="aField inline"><label>Předmět</label><select value={q.subject} onChange={e => setQ({ ...q, subject: Number(e.target.value) })}>{SUBJECTS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}</select></div>
                    <div className="aField inline"><label>Typ</label><select value={q.type} onChange={e => setType(Number(e.target.value))}>{QTYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}</select></div>
                    <div className="aField inline"><label>Téma</label><input value={q.topic} onChange={e => setQ({ ...q, topic: e.target.value })} /></div>
                    <div className="aField inline"><label>Obtížnost (1–5)</label><input type="number" min={1} max={5} value={q.difficulty} onChange={e => setQ({ ...q, difficulty: Number(e.target.value) })} /></div>
                </div>
                <div className="aField"><label>Obrázek (volitelné)</label>
                    <div className="uploadField">
                        <input value={q.imageUrl ?? ""} onChange={e => setQ({ ...q, imageUrl: e.target.value })} placeholder="URL nebo nahraj obrázek →" />
                        <label className="aBtn ghost upInline" title="Nahrát obrázek">
                            {imgUploading ? "…" : "⬆"}
                            <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.currentTarget.value = ""; }} />
                        </label>
                    </div>
                </div>

                <h2 style={{ marginTop: "8px" }}>Možnosti</h2>
                {q.type === NONE_TYPE && <p className="noneHint">Správná odpověď je „Žádná z předchozích odpovědí není správná“ – do testu ji doplní systém automaticky. Níže zadej jen nesprávné možnosti.</p>}
                <div className="rowList">
                    {q.options.map((o, i) => (
                        <div key={o.id ?? o._k ?? i} className="optRow">
                            {q.type !== NONE_TYPE && <label className="toggle small" title="Správná odpověď"><input type="checkbox" checked={o.isCorrect} onChange={e => setOpt(i, { isCorrect: e.target.checked })} /><i className="slider" /></label>}
                            <input value={o.text} onChange={e => setOpt(i, { text: e.target.value })} placeholder={"Možnost " + (i + 1)} />
                            <span className="x" onClick={() => removeOpt(i)}>×</span>
                        </div>
                    ))}
                </div>
                <button className="aBtn ghost" style={{ marginTop: "8px" }} onClick={addOpt}>+ Přidat možnost</button>

                <div className="aField" style={{ marginTop: "14px" }}><label>Vysvětlení</label><textarea value={q.explanation} onChange={e => setQ({ ...q, explanation: e.target.value })} /></div>

                <div className="aModalActions">
                    <button className="aBtn ghost" onClick={onClose}>Zrušit</button>
                    <button className="aBtn" onClick={save}>Uložit otázku</button>
                </div>
            </div>
        </div>
    );
}
